import React, { useCallback, useMemo } from 'react';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { addFeatureFlagAccessed as featureFlagAccessedUFOv2 } from '@atlaskit/react-ufo/feature-flags-accessed';
import Suspense from '@atlaskit/react-ufo/suspense';
import { captureException } from '@sentry/react';
import { History } from 'history';
import { PreloadedQuery, graphql, usePreloadedQuery } from 'react-relay';

import { useAnalytics } from '@townsquare/analytics';
import { AppState, useAppStateStore } from '@townsquare/app-state-store';
import { CollabContext, type CollaborationContext } from '@townsquare/collab-context';
import { filterFalsyStrings, filterNull } from '@townsquare/filter-type';
import { useAsyncEffect } from '@townsquare/hooks';
import {
	setSentryAAID,
	setSentryCloudId,
	addFeatureFlagAccessed as featureFlagAccessedSentry,
	setSentryWorkspaceUuid,
} from '@townsquare/logging/sentry';
import { initBM3 } from '@townsquare/monitoring/bm3';
import { initReactUFO } from '@townsquare/monitoring/react-ufo';
import {
	initUFO,
	addFeatureFlagAccessed as featureFlagAccessedUFOv1,
} from '@townsquare/monitoring/ufo';
import { useOrgContextActions } from '@townsquare/org-context';
import { createOrgScopedPath } from '@townsquare/router/create-route';
import { preloadRouteComponent } from '@townsquare/router/preload';
import { matchRoute } from '@townsquare/router/primitives/match-route';
import type { GlobalHomePageRoute, SiteHomePageRoute } from '@townsquare/router/route-types';
import type { MatchedRoute } from '@townsquare/router/routing-types';
import type { CapabilityRouterFragment$key } from '@townsquare/router/types';
import { NoopRbacProvider } from '@townsquare/rovo/entitlement';
import { createSearchParamsFromCurrentUrl } from '@townsquare/search-params';
import { initConfigClient } from '@townsquare/stat-sig/config';
import { fg } from '@townsquare/stat-sig/gate';
import { featureGatesOptions, observabilityFilter } from '@townsquare/stat-sig/options';
import { setupPlatformGates } from '@townsquare/stat-sig/setup-platform';
import { useCurrentUser } from '@townsquare/user-store';
import { User } from '@townsquare/user-store/types';
import { useWorkspaceActions } from '@townsquare/workspace-store/actions';
import { WorkspaceDetails } from '@townsquare/workspace-store/types';

import { routes } from '../router/routes';
import { FullScreenSpaLoader } from '../ui/FullScreenSpaLoader';

import { BootstrapErrorBoundary } from './ErrorBoundary/BootstrapErrorBoundary';
import { NotFoundScreen } from './ErrorBoundary/scenarios';
import { BootstrapTeamsQuery } from './__generated__/BootstrapTeamsQuery.graphql';
import { preloadedQueryRef } from './preload';

export type BootstrapResult = {
	orgId?: string;
	cloudIds: string[];
	workspaceIds: string[];
	workspaceUuids: string[];
	workspace?: WorkspaceDetails;
	user: User;
	relayKey: CapabilityRouterFragment$key | undefined;
	collabContext?: CollaborationContext | null;
};

type BootstrapProps = {
	children: (data: BootstrapResult) => JSX.Element;
	matchedRoute: MatchedRoute | null;
	history: History<unknown>;
	queryRef: PreloadedQuery<BootstrapTeamsQuery>;
};

function isGlobalRoute(route: MatchedRoute['route']): route is GlobalHomePageRoute {
	return (route as GlobalHomePageRoute | SiteHomePageRoute)?.routeType === 'global';
}

function BootstrapResolution(props: BootstrapProps): JSX.Element {
	const data = usePreloadedQuery(
		graphql`
			query BootstrapTeamsQuery($context: OrganizationContextInput) {
				bootstrap(organizationContext: $context) {
					orgId
					cloudIds
					collabContext {
						cloudIds
						isMultiSite
						hasMultipleUnits
						userbaseAri
					}
					userIsOrgAdmin
					workspaces {
						id
						uuid
						cloudId
						type
						...storeHomeWorkspace
						...CapabilityRouterFragment
					}
				}
			}
		`,
		props.queryRef,
	);

	const [{ appState }, { setAppState }] = useAppStateStore();

	const orgId = data.bootstrap?.orgId;
	const cloudIds = data.bootstrap?.cloudIds?.filter(filterFalsyStrings) ?? [];
	const workspaces = data.bootstrap?.workspaces?.filter(filterNull) ?? [];
	const workspaceIds =
		workspaces?.map((workspace) => workspace?.id).filter(filterFalsyStrings) ?? [];
	const workspaceUuids =
		workspaces?.map((workspace) => workspace?.uuid).filter(filterFalsyStrings) ?? [];

	const orgContextActions = useOrgContextActions();
	if (orgId) {
		orgContextActions.setOrgContext({
			orgId,
			cloudIds,
			workspaceIds,
			workspaceUuids,
			isOrgAdmin: data.bootstrap?.userIsOrgAdmin ?? false,
		});

		// This should only trigger for global routes
		const matchedRoute = props.matchedRoute;
		if (
			matchedRoute?.match.params.orgIdentifier !== orgId &&
			matchedRoute?.route &&
			isGlobalRoute(matchedRoute.route) &&
			matchedRoute.route.name !== 'not-found'
		) {
			const { path, pathPattern } = matchedRoute.route;
			const queryParams = Object.fromEntries(createSearchParamsFromCurrentUrl());
			props.history.replace(
				createOrgScopedPath(`${pathPattern}${path}`, orgId, queryParams, matchedRoute.match.params),
			);
		}
	}

	/**
	 * Titan is site-scoped
	 * The ordering here is as follows:
	 * 1. If the query has a context (aka we are hitting a deep link and we're passing a cloudId to the query), use that for searching for the workspace
	 * 2. If the query has no context, use the first cloudId in the list as that is ordered by the backend to be the most relevant (preference or otherwise based on the user)
	 * 3. If there are no cloudIds, default to null (aka no workspace exists for the current request)
	 *
	 * Scenario 1: User is on a deep link and the context is passed in the query
	 * The org has two sites, A and B.
	 * A has Titan, B does not.
	 * The user is on a deep link for site B.
	 * We won't find a workspace in this scenario, since we're only searching for the cloudId of site B, but the query will return the workspace for site A.
	 */
	const contextCloudId = props.queryRef.variables.context?.cloudId ?? cloudIds.at(0) ?? null;
	const workspace = workspaces
		// Exclude PRODUCT workspaces, treat them as no workspace
		.filter((workspace) => workspace.type === 'GLOBAL_EXPERIENCE')
		.find((workspace) => workspace.cloudId === contextCloudId);
	const workspaceActions = useWorkspaceActions();
	let workspaceDetails: WorkspaceDetails | undefined;
	if (workspace && orgId) {
		workspaceDetails = workspaceActions.initWorkspaceFromHome(workspace, {
			numWorkspaces: workspaceIds.length,
			userIsOrgAdmin: data.bootstrap?.userIsOrgAdmin ?? false,
			organisationId: orgId,
		});
	}

	const cloudContext = workspace?.cloudId ?? contextCloudId;
	const currentUser = useCurrentUser();
	const analytics = useAnalytics();
	const rovoWorkspaceUserbaseId = fg('townsquare_user_profile_userbase_id_sharding_key')
		? (data.bootstrap?.collabContext?.userbaseAri ?? null)
		: null;

	const collabContext = useMemo<CollaborationContext>(
		() => ({
			goalWorkspace: cloudContext,
			projectWorkspace: cloudContext,
			rovoWorkspace: cloudContext,
			rovoWorkspaceUserbaseId,
			contextId: `ari:cloud:townsquare::site/${cloudContext}`,
			orgCloudIds: data.bootstrap?.cloudIds?.filter(filterFalsyStrings) ?? null,
			numWorkspaces: workspaceIds.length,
			collabContextScoping: {
				collabContextCloudIds: data.bootstrap?.collabContext?.cloudIds.filter(filterFalsyStrings),
				isMultiSite: data.bootstrap?.collabContext?.isMultiSite,
				hasMultipleUnits: data.bootstrap?.collabContext?.hasMultipleUnits,
			},
		}),
		[
			cloudContext,
			data.bootstrap?.cloudIds,
			data.bootstrap?.collabContext?.cloudIds,
			data.bootstrap?.collabContext?.isMultiSite,
			data.bootstrap?.collabContext?.hasMultipleUnits,
			rovoWorkspaceUserbaseId,
			workspaceIds.length,
		],
	);

	setSentryAAID(currentUser.accountId);

	if (contextCloudId) setSentryCloudId(contextCloudId);
	if (workspaceDetails) setSentryWorkspaceUuid(workspaceDetails.UUID);

	const completeInit = useCallback(() => {
		// Initialize monitoring
		initBM3(analytics.getAnalyticsWebClient(), 'townsquare');
		initUFO(analytics.getAnalyticsWebClient(), 'townsquare');
		initReactUFO(analytics.getAnalyticsWebClient(), routes);

		// Set the analytics client ready
		analytics.setCurrentUserAndTenantInfo(
			currentUser.accountId,
			workspaceDetails?.UUID,
			cloudContext ?? undefined,
		);

		analytics.setTopLevelAttributes({ townsquareApp: 'teams' });

		setAppState(AppState.FULLY_INITIALISED);
	}, [analytics, currentUser.accountId, workspaceDetails?.UUID, cloudContext, setAppState]);

	useAsyncEffect(async () => {
		if (orgId) {
			try {
				const statsigGateInitPromise = FeatureGates.updateUser(
					featureGatesOptions,
					{
						atlassianAccountId: currentUser.accountId,
						tenantId: cloudContext ?? undefined,
						atlassianOrgId: orgId,
					},
					{
						targetApp: 'atlas_web',
						townsquareApp: 'teams',
						...(currentUser.locale ? { locale: currentUser.locale } : {}),
					},
				);

				const statsigExperimentInitPromise = initConfigClient({
					atlassianAccountId: currentUser.accountId,
					tenantId: cloudContext ?? undefined,
					atlassianOrgId: orgId,
				});

				await Promise.all([statsigGateInitPromise, statsigExperimentInitPromise]);
			} catch (e) {
				captureException(e);
			}
		}

		// If we have a matched route and it has a preload function, call it
		// This will warm up the route for faster navigation
		if (props.matchedRoute?.route) {
			preloadRouteComponent(props.matchedRoute.route);
		}

		setupPlatformGates({
			onFeatureFlagAccessed: (name, value) => {
				if (observabilityFilter(name)) {
					featureFlagAccessedUFOv1(name, value);
					featureFlagAccessedUFOv2(name, value);
					featureFlagAccessedSentry(name, value);
				}
			},
		});
		completeInit();
	}, [currentUser.accountId, currentUser.locale, completeInit, props.matchedRoute?.route]);

	if (appState === AppState.LOADING) {
		return <FullScreenSpaLoader />;
	}

	return (
		<CollabContext.Provider value={collabContext}>
			<NoopRbacProvider>
				{props.children({
					orgId,
					cloudIds,
					workspaceIds,
					workspaceUuids,
					workspace: workspaceDetails,
					user: currentUser,
					relayKey: workspace,
					collabContext,
				})}
			</NoopRbacProvider>
		</CollabContext.Provider>
	);
}

export function Bootstrap(
	props: Omit<BootstrapProps, 'queryRef' | 'matchedRoute'> & {
		queryRef?: PreloadedQuery<BootstrapTeamsQuery>;
	},
): React.JSX.Element {
	const urlSearch = Object.fromEntries(createSearchParamsFromCurrentUrl());
	const matchedRoute = matchRoute(routes, window.location.pathname, urlSearch);

	if (matchedRoute?.route.name === '404-page') {
		return <NotFoundScreen />;
	}

	return (
		<BootstrapErrorBoundary>
			<Suspense interactionName="spa-loader" fallback={<FullScreenSpaLoader />}>
				<BootstrapResolution
					{...props}
					queryRef={props.queryRef ?? preloadedQueryRef}
					matchedRoute={matchedRoute}
				/>
			</Suspense>
		</BootstrapErrorBoundary>
	);
}
